import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import debounce from 'lodash/debounce';

import Collection from 'core/model/Collection';
import Box from 'core/components/Box';
import Button from 'core/components/Button';
import Flex from 'core/components/Flex';
import InputGroup from 'core/form/components/InputGroup';

@observer
class Search extends Component {
  static propTypes = {
    // array of strings, filters to exclude from showing
    excludeFilters: PropTypes.array,
    autoFocus: PropTypes.bool,
    clearSelectionsOnChange: PropTypes.bool,
    collection: PropTypes.instanceOf(Collection).isRequired,
    inputProps: PropTypes.object,
    placeholder: PropTypes.string,
    onChange: PropTypes.func
  };

  static defaultProps = {
    excludeFilters: [],
    autoFocus: true,
    clearSelectionsOnChange: false,
    placeholder: 'Search',
    inputProps: {},
    onChange: null
  };

  state = { filterValue: undefined };

  static getDerivedStateFromProps(props, state) {
    const { collection, searchTerm } = props;
    const { filterValue } = state;

    // We are intentionally ignoring prop changes so this component controls filter state after initial change
    if (filterValue === undefined) {
      return { filterValue: searchTerm || collection.searchState?.value || collection.filterState };
    }
    return null;
  }

  debouncedSearchAction = debounce(() => {
    const { filterValue } = this.state;
    const { collection, onChange } = this.props;

    if (onChange) {
      onChange(filterValue);
    } else {
      // we expect the parent to handle this if providing its own change handler
      collection.filter(filterValue);
    }
  }, 300);

  componentWillUnmount() {
    // Cancel any pending debounced operations to prevent memory leaks
    if (this.debouncedSearchAction) {
      this.debouncedSearchAction.cancel();
    }
  }

  handleSearchChange = (e) => {
    this.setState(
      () => ({ filterValue: e.target.value }),
      () => this.debouncedSearchAction()
    );
  };

  handleSearchClear = () => {
    const { collection, clearSelectionsOnChange, onChange } = this.props;

    if (onChange) {
      onChange('');
    } else {
      collection.filter('');
    }

    if (clearSelectionsOnChange) {
      collection.clearSelection();
    }
  };

  render() {
    const {
      autoFocus,
      clearSelectionsOnChange,
      collection,
      excludeFilters,
      inputProps,
      placeholder,
      onChange,
      ...rest
    } = this.props;
    const { filterValue } = this.state;

    const rightElement = filterValue ? (
      <Flex height="100%" mr="5px">
        <Button icon="cross" onClick={this.handleSearchClear} minimal />
      </Flex>
    ) : undefined;

    return (
      <Box className="admin-table-search" {...rest}>
        <InputGroup
          value={filterValue}
          autoFocus={autoFocus}
          leftIcon={inputProps.leftElement ? undefined : 'search'}
          onChange={this.handleSearchChange}
          placeholder={placeholder}
          rightElement={rightElement}
          {...inputProps}
        />
      </Box>
    );
  }
}

export default Search;
